import { action, computed, observable } from "mobx";
import { useContext, useEffect } from "react";
import { QueryClient, useQueryClient } from "react-query";
import { CvpGetAnswersResponse } from "../../../../api/questionForm/CvpGetAnswers";
import { CvpGetQuestionFormLogsResponse } from "../../../../api/questionForm/CvpGetQuestionFormLogs";
import {
  FormQuestion,
  QuestionFormLog,
} from "../../../../api/questionForm/QuestionFormTypes";
import { getValueVersioner } from "../../../../WSUtils/getValueVersioner";
import { Store2 } from "../../../../WSUtils/Store";
import { invalidateQueries } from "../../../../WSUtils/useLatestQuery";
import { UserInfo } from "../../../../WSUtils/UserInfo";
import { UserInfoContext } from "../../../../WSUtils/UserInfoContext";
import { CVEditContext, CVEditContextStore } from "../../CVEditContext";
import { CVEQuestionStepContext } from "../CVEQuestionStepContext";
import { CVEQuestionStepContextStore } from "../CVEQuestionStepContextStore";
import { getQuestionFormToken } from "../getQuestionFormToken";
import { getQuestionType, QuestionType } from "../QuestionType";
import { useQuestionFormAnswers } from "../useQuestionFormAnswers";
import { useQuestionFormLog } from "../useQuestionFormLog";

export class CVETextQuestionStore extends Store2<any> {
  @observable value: string = "";

  valueVersioner = (() => {
    const valueVersioner = getValueVersioner({
      setValue: action((value) => {
        this.value = value;
      }),
      getBackendValue: () => {
        if (!this.answers?.ok) return;

        return {
          valueVersion: this.answers?.valueVersion,
          getVal: () => {
            const options = this.question?.options ?? [];
            const isNumber = options.length > 0 && options[0].isNumber;

            if (isNumber) return this?.answer?.numberAnswer;
            return this?.answer?.stringAnswer;
          },
        };
      },
    });

    this.dt.disposers.push(valueVersioner.dispose);

    return valueVersioner;
  })();

  onChange = (newValue: string) => {
    return this.valueVersioner.onChange({
      newValue,
      mutateBackend: this.mutateBackend,
      invalidateQuery: this.invalidateQuery,
    });
  };

  invalidateQuery = () =>
    invalidateQueries(this.queryClient, [
      "questionFormAPI.getAnswers",
      this.qfl?.id,
    ]);

  mutateBackend = (value: string) => {
    const t: CVETextQuestionStore = this;

    const questionFormAPI = t.userInfo.fullAPI.questionFormAPI;
    const token = getQuestionFormToken();

    if (value) {
      const type = getQuestionType(this.question);

      if (token) {
        return questionFormAPI.answerQuestionAnonymous({
          userInfo: t.userInfo,
          showSentryDialogOnError: true,
          token,
          answer: {
            relatedId: this.qfl.receiverRelatedId,
            relatedType: this.qfl.receiverRelatedType,
            questionId: this.question.id,
            questionOptionId: this.question.options[0].id,
            numberAnswer: type === QuestionType.NUMBER ? Number(value) : 0,
            stringAnswer: type === QuestionType.TEXT ? value : "",
            questionFormLog: this.qfl.id,
            clearAnswersFirst: false,
            id: this.question.id,
          },
        });
      } else {
        return questionFormAPI.answerQuestion({
          userInfo: t.userInfo,
          showSentryDialogOnError: true,

          //
          relatedId: this.qfl.receiverRelatedId,
          relatedType: this.qfl.receiverRelatedType,
          questionId: this.question.id,
          questionOptionId: this.question.options[0].id,
          numberAnswer: type === QuestionType.NUMBER ? Number(value) : 0,
          stringAnswer: type === QuestionType.TEXT ? value : "",
          questionFormLog: this.qfl.id,
          clearAnswersFirst: false,
          id: this.question.id,
        });
      }
    }

    if (typeof t.answer?.id === "number") {
      if (token) {
        return questionFormAPI.deleteAnswerAnonymous({
          userInfo: t.userInfo,
          showSentryDialogOnError: true,
          token,
          id: t.answer.id,
        });
      } else {
        return questionFormAPI.deleteAnswer({
          userInfo: t.userInfo,
          id: t.answer.id,
          showSentryDialogOnError: true,
        });
      }
    }
  };

  @computed get answer() {
    return this.answers?.items?.find((x) => x.questionId == this.question.id);
  }

  @observable forms: CvpGetQuestionFormLogsResponse;
  @observable answers: CvpGetAnswersResponse;
  @observable qfl: QuestionFormLog;

  userInfo: UserInfo;
  cvEditContext: CVEditContextStore;
  questionContext: CVEQuestionStepContextStore;
  queryClient: QueryClient;
  question: FormQuestion;

  _onRender = (question: FormQuestion) => {
    this.question = question;
    this.userInfo = useContext(UserInfoContext);
    this.cvEditContext = useContext(CVEditContext);
    this.questionContext = useContext(CVEQuestionStepContext);
    this.queryClient = useQueryClient();

    const questionFormAPI = this.userInfo.fullAPI.questionFormAPI;

    let { qfl } = useQuestionFormLog(question.questionFormId);

    const { answers } = useQuestionFormAnswers(qfl?.id);

    // put stuff in mobx state
    useEffect(
      action(() => {
        this.qfl = qfl;
        this.answers = answers;
      }),
      [qfl, answers]
    );
  };
}
